import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Header from "./Header";
import AdminSidebar from "./AdminSidebar";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { useApi } from "../hooks";
import { apiGetUser, method } from "../apiPath";
import keyNames from "../keyName";
import constants from "../common/constants";

const { ADMIN, USER } = constants;

const AdminLayout = () => {
  const hitApi = useApi();
  const hasFetched = useRef(false);
  const [fetchStarted, setFetchStarted] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const isMobile = useMediaQuery("(max-width:768px)");
  const location = useLocation();
  const storeData = useSelector((state) => state.mainReducer);
  const { userData, isLoading, forceLogOut } = storeData;
  const [open, setOpen] = useState(false);

  // Auto-open on desktop, auto-close on mobile
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    // Fetch the full profile if we have no userData OR the cached userData
    // is missing fields we now rely on (e.g. accessType — added later, so
    // pre-existing sessions need a refresh).
    const needsRefresh = !userData || userData.accessType === undefined;
    if (needsRefresh && !hasFetched.current) {
      hasFetched.current = true;
      setFetchStarted(true); // state update — won't be visible to sibling effects in same cycle
      hitApi(apiGetUser, null, method.get, keyNames.userData, null);
    }
  }, [userData, hitApi]);

  // Only mark auth checked AFTER the fetch actually started AND completed
  useEffect(() => {
    if (fetchStarted && isLoading === "noLoading") {
      setAuthChecked(true);
    }
  }, [fetchStarted, isLoading]);

  // Redirect to login only if explicitly logged out by server.
  // If the user originally arrived via a public share link, return them
  // there (so they can re-claim with a different account) — otherwise go
  // to the default login page. The flag is consumed once.
  if (forceLogOut) {
    const returnTo = sessionStorage.getItem("returnToShare");
    if (returnTo) sessionStorage.removeItem("returnToShare");
    return <Navigate to={returnTo || "/"} replace />;
  }

  // Show loading while fetching user data
  if (!userData && !authChecked) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated after auth check complete
  if (!userData && authChecked) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }

  // Role-based home redirect
  const USER_ALLOWED_PATHS = ["/app/my-photos", "/app/profile"];
  if (userData?.role === USER && !USER_ALLOWED_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/app/my-photos" replace />;
  }
  if (userData?.role === ADMIN && location.pathname === "/app") {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <Box className="vh-100" sx={{ display: "flex" }}>
      <Header open={open} setOpen={setOpen} />

      {/* Backdrop — mobile only, tap outside to close */}
      {isMobile && open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: "rgba(0,0,0,0.45)",
            zIndex: 1199,
          }}
        />
      )}

      <AdminSidebar open={open} isMobile={isMobile} setOpen={setOpen} />

      <Box
        className="transition"
        sx={{
          ml: isMobile ? 0 : (open ? "240px" : "66px"),
          mt: "64px",
          minHeight: "calc(-64px + 100vh)",
          width: "100%",
          borderRadius: "8px 8px 0px 0px",
          background: "#fff5f5",
          flexGrow: 1,
          p: isMobile ? 1.5 : 3,
          overflowY: "auto",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
export default AdminLayout;
